import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";
import "./StaffEdit.css";
import StaffEditorForm from "../components/staff/StaffEditorForm";
import StaffServicesAssignment from "../components/staff/StaffServicesAssignment";
import StaffKpiProgressCard from "../components/staff/StaffKpiProgressCard";
import StaffTrainingProfileCard from "../components/staff/StaffTrainingProfileCard";
import StaffClientSatisfactionCard from "../components/staff/StaffClientSatisfactionCard";
import ModalCloseX from "../components/common/ModalCloseX";
import InfoTooltip from "../components/common/InfoTooltip";
import SBINModal from "../components/modals/SBINModal";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import {
  getStaff,
  getStaffKpiProgress,
  getStaffLogin,
  removeStaffPhoto,
  getStaffSchedule,
  resetStaffCredentials,
  uploadStaffPhoto,
  updateStaff,
  updateStaffSchedule,
} from "../api/staffs";
import { useAuth } from "../context/AuthContext";
import useMyPermissions from "../hooks/useMyPermissions";

import {

  getUserPermissions,

  resetUserPermissions,

  updateUserPermissions,

} from "../api/salonPermissions";

import { listSalonUsers, updateSalonUser } from "../api/salonUsers";

import { SALON_ROLE_OPTIONS, normalizeSalonRole, roleLabel } from "../utils/salonRoles";
import { formatPermissionError } from "../utils/permissionErrorMessages";



const MANAGER_ROLES = [
  "admin",
  "host",
  "owner",
  "manager",
  "solo_technician",
  "super_admin",
  "superadmin",
];
const OWNER_REQUIRED_ERROR = "At least one user with Owner role is required for the salon.";
const PRIMARY_OWNER_ROLE_LOCKED_ERROR =
  "The primary owner role is locked for this salon. Transfer salon ownership first if you need to change this role.";


const defaultWeeklySchedule = () =>

  Array.from({ length: 7 }).map((_, idx) => ({

    day_of_week: idx,

    is_working: false,

    start_time: null,

    end_time: null,

  }));



const snapTo15 = (hhmm) => {

  if (!hhmm) return null;

  const [h, m] = hhmm.split(":");

  const hh = parseInt(h, 10);

  const mm = parseInt(m, 10);

  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;

  let snapped = Math.round(mm / 15) * 15;

  let hour = hh;

  if (snapped === 60) {

    snapped = 0;

    hour = (hour + 1) % 24;

  }

  return `${String(hour).padStart(2, "0")}:${String(snapped).padStart(2, "0")}`;

};



const PERMISSION_GROUPS = [
  {
    title: "Scheduling",
    items: [
      {
        key: "schedule.view_all",
        label: "View the full schedule",
        tooltip: "Lets this person see everyone's appointments on the salon schedule.",
      },
      {
        key: "schedule.view_own",
        label: "View your own schedule",
        tooltip: "Lets this person see only the appointments assigned to them.",
      },
      {
        key: "schedule.edit",
        label: "Create, edit, or cancel appointments",
        tooltip: "Lets this person add appointments, change appointments, and cancel appointments.",
      },
      {
        key: "schedule.cancel",
        label: "Show cancelled appointments",
        tooltip: "Lets this person turn on Show Cancelled and see cancelled appointments on the schedule.",
      },
      {
        key: "appointments.manage_options",
        label: "Appointment options",
        tooltip:
          "Lets this person use appointment pop-up buttons like check in, confirm, left message, edit, cancel, reschedule, and recurring.",
      },
    ],
  },
  {
    title: "Time Clock",
    items: [
      {
        key: "timeclock.view",
        label: "View and stamp time clocks",
        tooltip: "Lets this person see time clock records and clock staff in or out.",
      },
      {
        key: "timeclock.edit",
        label: "Edit time clock entries",
        tooltip: "Lets this person fix times or notes on time clock entries.",
      },
      {
        key: "timeclock.delete",
        label: "Delete time clock entries",
        tooltip: "Lets this person remove time clock entries that should not be kept.",
      },
    ],
  },
  {
    title: "Clients",
    items: [
      {
        key: "clients.manage",
        label: "Manage client records",
        tooltip: "Lets this person add clients and update client records.",
      },
      {
        key: "clients.view_contact",
        label: "View client contact info",
        tooltip: "Lets this person see client phone numbers, emails, and other contact details.",
      },
      {
        key: "clients.view_metrics",
        label: "View client visit and spend metrics",
        tooltip: "Lets this person see client visit history and spending totals.",
      },
      {
        key: "client_portal.manage",
        label: "Manage client portal and online booking",
        tooltip:
          "Lets this person manage online booking, client portal settings, and client portal requests.",
      },
    ],
  },
  {
    title: "Marketing",
    items: [
      {
        key: "marketing.manage",
        label: "Use marketing tools",
        tooltip:
          "Lets this person use the Marketing menu, including rewards, client feedback, campaigns, email tools, appointment emails, and duplicate email tools.",
      },
    ],
  },
  {
    title: "Emails",
    items: [
      {
        key: "emails.manage_opt_outs",
        label: "Manage email opt-outs and preferences",
        tooltip: "Lets this person update a client's email opt-out choices and message preferences.",
      },
    ],
  },
  {
    title: "Tickets & Services",
    items: [
      {
        key: "tickets.manage",
        label: "Manage tickets",
        tooltip: "Lets this person create, edit, and close tickets for checkout.",
      },
      {
        key: "tickets.delete",
        label: "Delete tickets",
        tooltip: "Lets this person delete tickets that should not stay in sales history.",
      },
      {
        key: "tickets.reopen",
        label: "Re-open tickets",
        tooltip: "Lets this person reopen closed tickets when a checkout needs a correction.",
      },
      {
        key: "tickets.refund",
        label: "Refund tickets",
        tooltip: "Lets this person issue refunds from tickets.",
      },
      {
        key: "gift_cards.manage",
        label: "Manage gift cards",
        tooltip: "Lets this person sell, adjust, and redeem gift cards.",
      },
      {
        key: "services.manage",
        label: "Manage services",
        tooltip: "Lets this person add and update services, pricing, categories, and online booking options.",
      },
    ],
  },
  {
    title: "Operations",
    items: [
      {
        key: "maintenance.manage",
        label: "Maintenance & Cleaning",
        tooltip:
          "Lets this person open Maintenance & Cleaning to manage salon cleaning tasks and check maintenance reports.",
      },
      {
        key: "inventory.manage",
        label: "Inventory",
        tooltip: "Lets this person open Inventory to add products, update stock counts, and track low supplies.",
      },
      {
        key: "cash_drawer.manage",
        label: "Cash drawer",
        tooltip: "Lets this person open the Cash Drawer page to enter and count drawer cash.",
      },
      {
        key: "walkin_queue.manage",
        label: "Walk-in queue slider",
        tooltip: "Lets this person use the walk-in queue slider on the schedule.",
      },
    ],
  },
  {
    title: "Workspace Rentals",
    items: [
      {
        key: "rentals.view",
        label: "View workspace rentals",
        tooltip: "Lets this person see workspace rental listings and booking details.",
      },
      {
        key: "rentals.manage",
        label: "Manage workspace rentals",
        tooltip: "Lets this person create and update workspace rentals.",
      },
      {
        key: "rentals.marketplace_manage",
        label: "Manage marketplace listings",
        tooltip: "Lets this person publish and update rental listings shown on the marketplace.",
      },
      {
        key: "rentals.reports.view",
        label: "View rental reports",
        tooltip: "Lets this person see reports for workspace rental activity.",
      },
    ],
  },
  {
    title: "Staff",
    items: [
      {
        key: "staff.manage",
        label: "Manage staff members",
        tooltip: "Lets this person open staff management tools and staff setup pages.",
      },
      {
        key: "staff.update",
        label: "Update Staff",
        tooltip: "Lets this person save changes to staff information, login details, photos, and schedules.",
      },
      {
        key: "staff.uses_time_clock",
        label: "Uses Time Clock",
        tooltip: "Lets this person turn the Uses Time Clock setting on or off for staff.",
      },
      {
        key: "staff.show_online_booking",
        label: "Show in Online Booking",
        tooltip: "Lets this person choose whether a staff member appears for online booking.",
      },
    ],
  },
  {
    title: "Platform Access",
    items: [
      {
        key: "platform.myboothshare_access",
        label: "Access Ellyanna Boothshare",
        tooltip: "Shows Ellyanna Boothshare links for this person so they can open workspace rental tools.",
      },
      {
        key: "platform.salonbusinesscpa_access",
        label: "Access Ellyanna Accounting",
        tooltip: "Shows Ellyanna Accounting links for this person so they can open accounting support tools.",
      },
    ],
  },
  {
    title: "Admin",
    items: [
      {
        key: "settings.manage",
        label: "Manage salon settings",
        tooltip: "Lets this person change salon setup, business rules, and system settings.",
      },
      {
        key: "users.manage_roles",
        label: "Manage user roles",
        tooltip: "Lets this person add users, change roles, and adjust permission access.",
      },
    ],
  },
  {
    title: "Rewards",
    items: [
      {
        key: "rewards.view",
        label: "View rewards program",
        tooltip: "Lets this person see the salon rewards program and reward activity.",
      },
    ],
  },
  {
    title: "Reports",
    items: [
      {
        key: "reports.view",
        label: "View reports",
        tooltip: "Lets this person open salon reports and view report results.",
      },
      {
        key: "schedule.estimated_revenue",
        label: "View estimated revenue",
        tooltip: "Lets this person see estimated money totals on the schedule.",
      },
    ],
  },
  {
    title: "Payroll",
    items: [
      {
        key: "payroll.view",
        label: "View payroll",
        tooltip: "Lets this person see payroll information.",
      },
      {
        key: "payroll.manage",
        label: "Manage payroll and pay stubs",
        tooltip: "Lets this person run payroll tools and manage pay stubs.",
      },
    ],
  },
  {
    title: "Training & Development",
    items: [
      {
        key: "training.view",
        label: "View training",
        tooltip: "Lets this person see training assigned to them.",
      },
      {
        key: "training.manage",
        label: "Manage training",
        tooltip: "Lets this person create training, assign it to staff, and review progress.",
      },
    ],
  },
];

const PERMISSION_GROUP_HELP = {
  Scheduling: "Controls schedule viewing and appointment changes.",
  "Time Clock": "Controls time clock viewing and edits.",
  Clients: "Controls client records, contact details, and metrics.",
  Emails: "Controls email preference and opt-out tools.",
  "Tickets & Services": "Controls tickets, services, refunds, and gift cards.",
  Operations: "Controls salon operations pages and schedule tools.",
  "Workspace Rentals": "Controls rental tools and rental reports.",
  Staff: "Controls staff management access.",
  "Platform Access": "Controls links to connected Ellyanna services.",
  Admin: "Controls salon settings and user role tools.",
  Rewards: "Controls rewards program access.",
  Reports: "Controls report access and estimated revenue.",
  Payroll: "Controls payroll and pay stub access.",
  "Training & Development": "Controls training tools and assignments.",
};


const OWNER_ONLY_PERMISSIONS = ["settings.manage", "users.manage_roles"];
const SCHEDULE_VIEW_ALL_PERMISSION = "schedule.view_all";
const SCHEDULE_VIEW_OWN_PERMISSION = "schedule.view_own";
const SCHEDULE_VIEW_PERMISSIONS = [
  SCHEDULE_VIEW_ALL_PERMISSION,
  SCHEDULE_VIEW_OWN_PERMISSION,
];



const STAFF_ONLY_ROLE_OPTIONS = [

  { value: "technician", label: roleLabel("technician") },

  { value: "apprentice", label: roleLabel("apprentice") },

];



const SALON_ROLE_DROPDOWN_OPTIONS = SALON_ROLE_OPTIONS.map((value) => ({
  value,
  label: roleLabel(value),
}));
const HELP_ALLOWED_ROLES = new Set(["owner", "manager", "super_admin", "superadmin"]);

const STAFF_GUIDE_TROUBLESHOOTING = [
  {
    title: "Staff not showing on schedule",
    body: "Confirm Weekly Schedule has working hours, the staff profile is active, and role permissions allow schedule visibility.",
  },
  {
    title: "Staff can’t be assigned to ticket services",
    body: "Check Schedule and Time Off for the appointment time. Staff must be working and not blocked off during that time window.",
  },
  {
    title: "Online booking not showing this staff",
    body: "Verify services are assigned and marked to show online, and confirm the staff is available during the requested booking hours.",
  },
];


const resolveLinkedUserId = (staffRecord) => {

  if (!staffRecord) return null;

  if (staffRecord.user_id) return staffRecord.user_id;

  if (staffRecord.userId) return staffRecord.userId;

  if (staffRecord.user?.id) return staffRecord.user.id;

  if (staffRecord.user?.user_id) return staffRecord.user.user_id;

  if (staffRecord.user?.userId) return staffRecord.user.userId;

  return null;

};



const resolveLinkedIdList = (values, fallbackValue = null) => {

  const rawValues = Array.isArray(values) ? values : [];

  const normalized = rawValues
    .map((value) => Number(value))
    .filter((value) => Number.isInteger(value));

  if (normalized.length > 0) {

    return Array.from(new Set(normalized)).sort((a, b) => a - b);

  }

  const fallback = Number(fallbackValue);

  return Number.isInteger(fallback) ? [fallback] : [];

};

const joinFullName = (firstName, lastName) =>
  [String(firstName || "").trim(), String(lastName || "").trim()]
    .filter(Boolean)
    .join(" ");

export function StaffEditPanel({
  staffId,

  embedded = false,

  onDone,

  onCancel,

}) {

  const navigate = useNavigate();

  const { token, role: editorRole, user: currentUser, updateCurrentUser } = useAuth();
  const { permissions: effectivePermissions } = useMyPermissions({
    enabled: Boolean(token && currentUser?.salon_id),
  });
  const [staff, setStaff] = useState(null);

  const [weeklySchedule, setWeeklySchedule] = useState(defaultWeeklySchedule());

  const [loading, setLoading] = useState(true);

  const [savingInfo, setSavingInfo] = useState(false);

  const [savingSchedule, setSavingSchedule] = useState(false);

  const [error, setError] = useState("");

  const [success, setSuccess] = useState("");

  const [saveStatus, setSaveStatus] = useState(null);

  const saveStatusTimeoutRef = useRef(null);

  const [linkedUserId, setLinkedUserId] = useState(null);
  const [kpiProgress, setKpiProgress] = useState(null);
  const [loadingKpiProgress, setLoadingKpiProgress] = useState(false);
  const [kpiProgressError, setKpiProgressError] = useState("");
  const [savingKpiTargets, setSavingKpiTargets] = useState(false);
  const [membership, setMembership] = useState(null);
  const [staffPermissionToggles, setStaffPermissionToggles] = useState({
    enableCancelAppointments: false,
    enableEstimatedRevenue: false,
    enableSalonSpeakModules: false,
  });
  const [loadingStaffPermissionToggles, setLoadingStaffPermissionToggles] =
    useState(false);
  const [loginUsername, setLoginUsername] = useState("");
  const [loginEmail, setLoginEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loginStatus, setLoginStatus] = useState(null);
  const [highlightedSectionId, setHighlightedSectionId] = useState("");
  const canEditScheduleOrder = ["owner", "manager"].includes(
    (editorRole || "").toLowerCase()
  );
  const hasGlobalStaffOptionAccess =
    Boolean(currentUser?.is_super_admin) ||
    ["admin", "super_admin", "superadmin"].includes((editorRole || "").toLowerCase());
  const canEditUsesTimeClock =
    hasGlobalStaffOptionAccess ||
    effectivePermissions === null ||
    Boolean(effectivePermissions?.["staff.uses_time_clock"]);
  const canEditOnlineBookingVisibility =
    hasGlobalStaffOptionAccess ||
    effectivePermissions === null ||
    Boolean(effectivePermissions?.["staff.show_online_booking"]);
  const canViewStaffHelp = HELP_ALLOWED_ROLES.has((editorRole || "").toLowerCase());
  const sectionHighlightTimeoutRef = useRef(null);
  const linkedSalonIds = useMemo(
    () => resolveLinkedIdList(staff?.linked_salon_ids, staff?.salon_id),
    [staff?.linked_salon_ids, staff?.salon_id]
  );
  const linkedBusinessIds = useMemo(
    () => resolveLinkedIdList(staff?.linked_business_ids),
    [staff?.linked_business_ids]
  );


  const loadData = useCallback(

    async ({ suppressLoading = false } = {}) => {

      if (!staffId) {

        setStaff(null);

        if (!suppressLoading) {

          setLoading(false);

        }

        return;

      }

      if (!suppressLoading) {

        setLoading(true);

      }

      setError("");

      setSaveStatus(null);

      try {

        const staffData = await getStaff(staffId, token);

        const staffLinkedUserId = resolveLinkedUserId(staffData);

        setStaff(staffData);

        setLinkedUserId(staffLinkedUserId);

        setLoginEmail(staffData.email || "");

        if (staffData.user_id) {

          try {

            const loginData = await getStaffLogin(staffId, token);

            setLoginUsername(loginData.username || "");

            setLoginEmail(loginData.email || staffData.email || "");

          } catch (err) {

            setLoginUsername((prev) => prev || staffData.email || "");

          }

        } else {

          setLoginUsername("");

        }

        setLoginStatus(null);

        const scheduleData = await getStaffSchedule(staffId, token);

        const sanitizedSchedule =

          scheduleData?.weekly_schedule && Array.isArray(scheduleData.weekly_schedule)

            ? scheduleData.weekly_schedule.map((item) => ({

              ...item,

              start_time: snapTo15(item.start_time),

              end_time: snapTo15(item.end_time),

            }))

            : defaultWeeklySchedule();

        setWeeklySchedule(sanitizedSchedule);

      } catch (err) {

        setError(err.message || "Failed to load data");

      } finally {

        if (!suppressLoading) {

          setLoading(false);

        }

      }

    },

    [staffId, token]

  );



  useEffect(() => {

    loadData();

  }, [loadData]);

  const loadKpiProgress = useCallback(async () => {
    if (!staffId) {
      setKpiProgress(null);
      setKpiProgressError("");
      return;
    }
    setLoadingKpiProgress(true);
    setKpiProgressError("");
    try {
      const data = await getStaffKpiProgress(staffId, token);
      setKpiProgress(data);
    } catch (err) {
      setKpiProgress(null);
      setKpiProgressError(err?.message || "Unable to load KPI progress.");
    } finally {
      setLoadingKpiProgress(false);
    }
  }, [staffId, token]);

  useEffect(() => {
    loadKpiProgress();
  }, [loadKpiProgress]);


  useEffect(() => {

    if (!success) return;

    const timer = setTimeout(() => setSuccess(""), 3000);

    return () => clearTimeout(timer);

  }, [success]);



  const loadMembership = useCallback(async () => {

    if (!linkedUserId) {

      setMembership(null);

      return;

    }

    try {

      const list = await listSalonUsers(token);

      const target = Array.isArray(list)

        ? list.find((member) => member.user_id === linkedUserId)

        : null;

      setMembership(target || null);

    } catch (err) {

      setMembership(null);

    }

  }, [linkedUserId, token]);



  useEffect(() => {
    if (!linkedUserId) {
      setMembership(null);
      setStaffPermissionToggles({
        enableCancelAppointments: false,
        enableEstimatedRevenue: false,
        enableSalonSpeakModules: false,
      });
      return;
    }
    loadMembership();
  }, [linkedUserId, loadMembership]);

  const loadStaffPermissionToggles = useCallback(async () => {
    if (!linkedUserId) {
      setStaffPermissionToggles({
        enableCancelAppointments: false,
        enableEstimatedRevenue: false,
        enableSalonSpeakModules: false,
      });
      return;
    }
    setLoadingStaffPermissionToggles(true);
    try {
      const bundle = await getUserPermissions(linkedUserId, token);
      setStaffPermissionToggles({
        enableCancelAppointments: Boolean(bundle?.effective?.["schedule.cancel"]),
        enableEstimatedRevenue: Boolean(
          bundle?.effective?.["schedule.estimated_revenue"]
        ),
        enableSalonSpeakModules: Boolean(bundle?.effective?.["training.view"]),
      });
    } catch (err) {
      setStaffPermissionToggles({
        enableCancelAppointments: false,
        enableEstimatedRevenue: false,
        enableSalonSpeakModules: false,
      });
    } finally {
      setLoadingStaffPermissionToggles(false);
    }
  }, [linkedUserId, token]);

  useEffect(() => {
    loadStaffPermissionToggles();
  }, [loadStaffPermissionToggles]);


  useEffect(() => {
    return () => {
      if (saveStatusTimeoutRef.current) {
        clearTimeout(saveStatusTimeoutRef.current);
      }
      if (sectionHighlightTimeoutRef.current) {
        clearTimeout(sectionHighlightTimeoutRef.current);
      }
    };
  }, []);


  const staffForForm = useMemo(() => {

    if (!staff) return null;

    const roleValue = membership?.role ?? staff.role;

    return { ...staff, role: normalizeSalonRole(roleValue) };

  }, [staff, membership?.role]);



  const activeRoleOptions = useMemo(

    () => (linkedUserId ? SALON_ROLE_DROPDOWN_OPTIONS : STAFF_ONLY_ROLE_OPTIONS),

    [linkedUserId]

  );



  const isPrimaryOwnerMembership = Boolean(membership?.is_primary_owner);

  const modalText = "text-base md:text-lg";
  const labelText =

    "text-sm md:text-base font-semibold tracking-wide text-slate-500";

  const fieldClass =

    "h-11 md:h-12 px-4 text-base md:text-lg rounded-xl border border-slate-300";

  const btnBase = "h-11 md:h-12 px-6 text-base md:text-lg rounded-full";

  const cardClass = "rounded-2xl border border-slate-200 bg-white shadow-sm p-6";

  const sectionTitleClass = "text-lg md:text-xl font-semibold text-slate-900";



  const handleStaffSave = async ({ info, permissionToggles }) => {
    if (!staffId || !info?.display_name?.trim()) return;
    setSavingInfo(true);
    setError("");

    setSuccess("");

    try {

      const { role, ...rest } = info;

      const normalizedRole = normalizeSalonRole(role);

      const staffPayload = { ...rest };

      if (!linkedUserId) {

        staffPayload.role = normalizedRole;

      }



      await updateStaff(staffId, staffPayload, token);

      const nextFullName = joinFullName(info.first_name, info.last_name);
      if (

        linkedUserId &&

        currentUser?.id &&

        linkedUserId === currentUser.id &&

        nextFullName
      ) {

        updateCurrentUser({

          full_name: nextFullName,
          name: nextFullName,
        });

      }

      if (linkedUserId) {
        const membershipRole = membership
          ? normalizeSalonRole(membership.role)
          : null;
        if (membershipRole !== normalizedRole) {
          await updateSalonUser(linkedUserId, { role: normalizedRole }, token);
          await loadMembership();
        }
      }
      if (linkedUserId && permissionToggles) {
        const latestBundle = await getUserPermissions(linkedUserId, token);
        const nextOverrides = { ...(latestBundle?.overrides || {}) };

        const applyPermissionToggle = (key, enabled) => {
          const defaultValue = Boolean(latestBundle?.defaults?.[key]);
          const nextValue = Boolean(enabled);
          if (nextValue === defaultValue) {
            delete nextOverrides[key];
            return;
          }
          nextOverrides[key] = nextValue;
        };

        applyPermissionToggle(
          "schedule.cancel",
          permissionToggles.enableCancelAppointments
        );
        applyPermissionToggle(
          "schedule.estimated_revenue",
          permissionToggles.enableEstimatedRevenue
        );
        applyPermissionToggle(
          "training.view",
          permissionToggles.enableSalonSpeakModules
        );

        const permissionResult = await updateUserPermissions(
          linkedUserId,
          nextOverrides,
          token
        );
        setStaffPermissionToggles({
          enableCancelAppointments: Boolean(
            permissionResult?.effective?.["schedule.cancel"]
          ),
          enableEstimatedRevenue: Boolean(
            permissionResult?.effective?.["schedule.estimated_revenue"]
          ),
          enableSalonSpeakModules: Boolean(
            permissionResult?.effective?.["training.view"]
          ),
        });
      }
      setSaveStatus({ type: "success", message: "Changes have been saved." });
      if (saveStatusTimeoutRef.current) {

        clearTimeout(saveStatusTimeoutRef.current);

      }

      saveStatusTimeoutRef.current = setTimeout(() => {

        setSaveStatus(null);

      }, 3000);

      setSuccess("Changes have been saved.");

      await loadData({ suppressLoading: true });

      onDone?.();

    } catch (err) {
      const detail = err?.response?.data?.detail;
      const detailCode = typeof detail === "object" ? detail?.code : detail;
      const detailMessage = typeof detail === "object" ? detail?.message : detail;
      const message =
        detailCode === "LAST_OWNER_REQUIRED"
          ? OWNER_REQUIRED_ERROR
          : detailMessage === "Primary owner membership cannot be modified"
          ? PRIMARY_OWNER_ROLE_LOCKED_ERROR
          : detailMessage === "Managers cannot modify owners"
          ? "Managers cannot change the role of an owner."
          : detailMessage === "Managers cannot promote members to owner"
          ? "Managers cannot assign the owner role."
          : formatPermissionError(err, detailMessage || err.message || "Failed to save staff info");
      setSaveStatus({ type: "error", message });
    } finally {
      setSavingInfo(false);
    }
  };


  const handleSaveKpiTargets = async (payload) => {
    if (!staffId) return;
    setSavingKpiTargets(true);
    setError("");
    setSuccess("");
    try {
      const updated = await updateStaff(staffId, payload, token);
      setStaff(updated);
      await loadKpiProgress();
      setSuccess("KPI targets updated.");
    } catch (err) {
      setError(formatPermissionError(err, "Unable to save KPI targets."));
    } finally {
      setSavingKpiTargets(false);
    }
  };


  const handleSaveSchedule = async (schedule) => {
    if (!staffId || !Array.isArray(schedule)) return;
    setSavingSchedule(true);

    setError("");

    setSuccess("");

    try {

      await updateStaffSchedule(staffId, schedule, token);

      setSuccess("Changes have been saved.");

      await loadData({ suppressLoading: true });

      onDone?.();

    } catch (err) {

      setError(formatPermissionError(err, "Failed to save schedule"));

    } finally {

      setSavingSchedule(false);

    }

  };



  const handleUpdateLogin = async () => {
    if (!staffId || !staff?.user_id) return;
    if (newPassword && newPassword !== confirmPassword) {

      setLoginStatus({ type: "error", message: "Passwords do not match." });

      return;

    }

    setLoginStatus(null);

    try {

      const trimmedEmail = loginEmail.trim().toLowerCase();

      const currentStaffEmail = (staff?.email || "").trim().toLowerCase();

      if (trimmedEmail && trimmedEmail !== currentStaffEmail) {

        await updateStaff(staffId, { email: trimmedEmail }, token);

      }

      const payload = {
        username: loginUsername?.trim() ?? "",

      };

      if (newPassword) {

        payload.password = newPassword;

      }

      await resetStaffCredentials(staffId, payload, token);

      setLoginStatus({ type: "success", message: "Login updated." });

      setNewPassword("");

      setConfirmPassword("");

      await loadData({ suppressLoading: true });

    } catch (err) {

      const detail = err?.response?.data?.detail;

      const message =

        detail === "USERNAME_TAKEN"

          ? "That username is already taken."

          : detail === "EMAIL_ALREADY_IN_USE"

          ? "That email address is already assigned to another team member. Please use a different email address."

          : formatPermissionError(
              err,
              err?.response?.data?.message ||
                err?.message ||
                "Unable to update login credentials."
            );

      setLoginStatus({ type: "error", message });

    }
  };

  const handleUploadPhoto = useCallback(
    async (file) => {
      if (!staffId || !file) return "";
      const result = await uploadStaffPhoto(staffId, file, token);
      const nextUrl = result?.photo_url || "";
      setStaff((prev) => (prev ? { ...prev, photo_url: nextUrl } : prev));
      return nextUrl;
    },
    [staffId, token]
  );

  const handleRemovePhoto = useCallback(async () => {
    if (!staffId) return "";
    const result = await removeStaffPhoto(staffId, token);
    const nextUrl = result?.photo_url || "";
    setStaff((prev) => (prev ? { ...prev, photo_url: nextUrl } : prev));
    return nextUrl;
  }, [staffId, token]);

  const scrollToSection = useCallback((id) => {
    const target = typeof document !== "undefined" ? document.getElementById(id) : null;
    if (!target) return false;
    target.scrollIntoView({ behavior: "smooth", block: "start" });
    return true;
  }, []);

  const jumpToSectionWithHighlight = useCallback(
    (id) => {
      const jumped = scrollToSection(id);
      if (!jumped) return;
      setHighlightedSectionId(id);
      if (sectionHighlightTimeoutRef.current) {
        clearTimeout(sectionHighlightTimeoutRef.current);
      }
      sectionHighlightTimeoutRef.current = setTimeout(() => {
        setHighlightedSectionId((current) => (current === id ? "" : current));
      }, 1500);
    },
    [scrollToSection]
  );

  const helpSections = useMemo(() => {
    const sections = [];
    if (staff?.user_id) {
      sections.push({
        id: "login",
        title: "Login",
        description: "How this staff member signs in and how you maintain secure access.",
        anchorId: "section-login",
        bullets: [
          "Staff can sign in with username and email if your setup supports both.",
          "This login email is synced with Staff Email in Details.",
          "Use passwords with at least 8 characters and reset during turnover.",
          "Do not share a single login across multiple team members.",
        ],
        tips: [
          "If login fails, confirm username/email first before resetting password.",
          "Use unique credentials so audit history stays accurate.",
        ],
      });
    }
    sections.push(
      {
        id: "details",
        title: "Details",
        description: "Core identity and role settings used throughout Ellyanna.",
        anchorId: "section-details",
        bullets: [
          "Display Name is what front desk and technicians see on schedule and tickets.",
          "Role controls permission scope and reporting access.",
          "Email and phone support communication and account recovery.",
          "Choose the closest role to actual daily responsibilities.",
        ],
        tips: [
          "Avoid nickname changes that confuse clients and staff.",
          "Review role after promotions to avoid missing permissions.",
        ],
      },
      {
        id: "settings",
        title: "Settings",
        description: "Feature toggles that change how the staff member appears in operations.",
        anchorId: "section-settings",
        bullets: [
          "Use toggles to enable staff-specific tools such as Time Clock participation.",
          "Deactivate instead of deleting when staff leave to preserve history.",
          "A deactivated staff profile should stop new schedule booking and login access while keeping prior records.",
          "Historical reports remain intact even when active settings change.",
        ],
        tips: [
          "If a staff member is missing in a tool, verify toggle settings first.",
        ],
      },
      {
        id: "profile",
        title: "Profile",
        description: "Optional profile details used for internal and client-facing context.",
        anchorId: "section-profile",
        bullets: [
          "Use profile fields to document skills, languages, and experience.",
          "Keep profile notes professional and business-relevant.",
          "Avoid sensitive personal data that is not needed for operations.",
        ],
        tips: [
          "Profile quality helps bookings route to the right technician.",
        ],
      },
      {
        id: "schedule",
        title: "Schedule",
        description: "Working hours and availability logic that powers booking decisions.",
        anchorId: "section-schedule",
        bullets: [
          "Set weekly working hours and keep them current.",
          "Not Working or Time Off blocks appointment placement during those windows.",
          "Availability here directly affects ticket service assignment validation.",
        ],
        tips: [
          "When assignments fail, check overlapping time off first.",
        ],
      },
      {
        id: "services",
        title: "Services",
        description: "Controls what this technician can perform and what appears in booking flows.",
        anchorId: "section-services",
        bullets: [
          "Assign only services this staff member can perform confidently.",
          "Assigned services drive online booking choices and front desk options.",
          "Keep durations and overrides accurate to avoid schedule conflicts.",
        ],
        tips: [
          "Service assignment mismatches are a common cause of wrong bookings.",
        ],
      }
    );

    if (linkedUserId) {
      sections.push({
        id: "permissions",
        title: "Permissions",
        description: "Fine-tune what this user can see and change in the system.",
        anchorId: "section-permissions",
        bullets: [
          "Permission categories map to areas such as schedule, tickets, reports, and settings.",
          "Start from role defaults and only add overrides when necessary.",
          "Use least-privilege access for security and clearer accountability.",
        ],
        tips: [
          "Grant access in small increments and retest workflows immediately.",
        ],
      });
    }

    return sections;
  }, [linkedUserId, staff?.user_id]);


  const content = (
    <div className={`${modalText} space-y-6`}>

      {saveStatus?.type === "success" && (

        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-700 text-sm">

          {saveStatus.message}

        </div>

      )}

      {saveStatus?.type === "error" && (

        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm flex items-start justify-between gap-3">

          <div className="flex items-start gap-2">

            <span>{saveStatus.message}</span>

          </div>

          <button

            type="button"

            className="text-xs text-rose-700/80 hover:text-rose-800 underline"

            onClick={() => setSaveStatus(null)}

          >

            Dismiss

          </button>

        </div>

      )}

      {error && saveStatus?.type !== "error" && (

        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm flex items-start gap-2">

          <span>{error}</span>

        </div>

      )}

      {success && (

        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-700 text-sm">

          {success}

        </div>

      )}



      {staff?.user_id && (
        <div
          id="section-login"
          className={`${cardClass} scroll-mt-24 ${highlightedSectionId === "section-login" ? "ring-2 ring-rose-300 ring-offset-2 transition-shadow duration-200" : ""}`}
        >
          <div className="mb-4">
            <div className={`${sectionTitleClass} flex items-center gap-2`}>
              Login Credentials
              {canViewStaffHelp && (
                <InfoTooltip
                  text="How this staff member signs in."
                  label="Login Credentials"
                  maxWidth={260}
                />
              )}
            </div>
            <div className="text-sm md:text-base text-slate-500">
              Manage how this staff member signs in.
            </div>
            {canViewStaffHelp && (
              <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                Tip: Use unique credentials for each staff member so security and audit logs stay accurate.
              </div>
            )}
          </div>


          {loginStatus?.type === "error" && (

            <div className="mb-3 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-2 text-rose-700 text-sm">

              {loginStatus.message}

            </div>

          )}

          {loginStatus?.type === "success" && (

            <div className="mb-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-emerald-700 text-sm">

              {loginStatus.message}

            </div>

          )}



          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">

            <div className="flex flex-col gap-1">
              <label
                htmlFor="login_username"
                className={`${labelText} flex items-center gap-2`}
              >
                Username
                {canViewStaffHelp && (
                  <InfoTooltip
                    text="Staff can log in with username or email."
                    label="Username"
                    maxWidth={260}
                  />
                )}
              </label>
              <input
                id="login_username"

                name="login_username"

                autoComplete="username"

                type="text"

                className={`w-full ${fieldClass} focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10`}

                value={loginUsername}

                onChange={(e) => setLoginUsername(e.target.value)}

                placeholder="username"

              />

              <div className="text-xs md:text-sm text-slate-500">

                User can log in with username or email.

              </div>

            </div>



            <div className="flex flex-col gap-1">
              <label
                htmlFor="login_email"
                className={`${labelText} flex items-center gap-2`}
              >
                Email
                {canViewStaffHelp && (
                  <InfoTooltip
                    text="This email is used for login and staff notifications."
                    label="Email"
                    maxWidth={260}
                  />
                )}
              </label>
              <input
                id="login_email"

                name="login_email"

                autoComplete="email"

                type="email"

                className={`w-full ${fieldClass} focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10`}

                value={loginEmail}

                onChange={(e) => setLoginEmail(e.target.value)}

                placeholder="email@example.com"

              />

              <div className="text-xs md:text-sm text-slate-500">

                This email is kept in sync with Staff Email.

              </div>

            </div>



            <div className="flex flex-col gap-1">
              <label
                htmlFor="login_new_password"
                className={`${labelText} flex items-center gap-2`}
              >
                New Password
                {canViewStaffHelp && (
                  <InfoTooltip
                    text="Leave blank to keep the current password."
                    label="New Password"
                    maxWidth={260}
                  />
                )}
              </label>
              <input

                id="login_new_password"

                name="login_new_password"

                autoComplete="new-password"

                type="password"

                className={`w-full ${fieldClass} focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10`}

                value={newPassword}

                onChange={(e) => setNewPassword(e.target.value)}

                placeholder="At least 8 characters"

              />

            </div>



            <div className="flex flex-col gap-1">

              <label

                htmlFor="login_confirm_password"

                className={`${labelText} flex items-center gap-2`}

              >

                Confirm Password
                {canViewStaffHelp && (
                  <InfoTooltip
                    text="Type the same new password again."
                    label="Confirm Password"
                    maxWidth={260}
                  />
                )}

              </label>

              <input

                id="login_confirm_password"

                name="login_confirm_password"

                autoComplete="new-password"

                type="password"

                className={`w-full ${fieldClass} focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10`}

                value={confirmPassword}

                onChange={(e) => setConfirmPassword(e.target.value)}

                placeholder="Repeat password"

              />

            </div>

          </div>



          <div className="mt-4 flex items-center justify-end">

            <button

              type="button"

              className={`${btnBase} font-semibold text-white transition bg-slate-900 hover:bg-slate-800`}

              onClick={handleUpdateLogin}

            >

              Update Login

            </button>

          </div>

        </div>

      )}



      <StaffEditorForm
        mode="edit"
        initialStaff={staffForForm}
        initialSchedule={weeklySchedule}
        onSave={handleStaffSave}
        onSaveSchedule={handleSaveSchedule}
        saving={savingInfo}
        savingSchedule={savingSchedule}
        error=""
        showCancel={!!onCancel}
        onCancel={onCancel}
        compact={false}
        roleOptions={activeRoleOptions}
        roleLocked={isPrimaryOwnerMembership}
        roleLockNote={
          isPrimaryOwnerMembership
            ? "Primary owner role is locked for this salon. Transfer ownership first if you need another owner to manage this location."
            : ""
        }
        canEditScheduleOrder={canEditScheduleOrder}
        onUploadPhoto={handleUploadPhoto}
        onRemovePhoto={handleRemovePhoto}
        highlightSectionId={highlightedSectionId}
        showInlineHelp={canViewStaffHelp}
        showTechnicianPermissionToggles={Boolean(linkedUserId)}
        permissionToggles={staffPermissionToggles}
        loadingPermissionToggles={loadingStaffPermissionToggles}
        canEditUsesTimeClock={canEditUsesTimeClock}
        canEditOnlineBookingVisibility={canEditOnlineBookingVisibility}
        beforeProfileContent={
          <>
            {staffId ? (
              <StaffKpiProgressCard
                title="Targeted KPI"
                subtitle="Set per-staff KPI targets and track current month achievement before reviewing training and development."
                progress={kpiProgress}
                loading={loadingKpiProgress}
                error={kpiProgressError}
                editable
                initialTargets={staff}
                onSaveTargets={handleSaveKpiTargets}
                saving={savingKpiTargets}
                cardClass={cardClass}
              />
            ) : null}
            {linkedUserId ? (
              <StaffTrainingProfileCard
                userId={linkedUserId}
                token={token}
                cardClass={cardClass}
              />
            ) : null}
            {staffId ? (
              <StaffClientSatisfactionCard
                staffId={staffId}
                token={token}
                cardClass={cardClass}
              />
            ) : null}
          </>
        }
        uiClasses={{ modalText, labelText, fieldClass, btnBase, cardClass }}
      />
      <div
        id="section-services"
        className={`scroll-mt-24 ${highlightedSectionId === "section-services" ? "ring-2 ring-rose-300 ring-offset-2 rounded-2xl transition-shadow duration-200" : ""}`}
      >
        <StaffServicesAssignment
          staffId={staffId}
          salonId={staff?.salon_id}
          token={token}
          editorRole={editorRole}
          highlightSectionId={highlightedSectionId}
          showInlineHelp={canViewStaffHelp}
          uiClasses={{ modalText, labelText, fieldClass, btnBase, cardClass }}
        />
      </div>
      {linkedUserId && (
        <div
          id="section-permissions"
          className={`scroll-mt-24 ${highlightedSectionId === "section-permissions" ? "ring-2 ring-rose-300 ring-offset-2 rounded-2xl transition-shadow duration-200" : ""}`}
        >
          <StaffPermissionsSection
            userId={linkedUserId}
            editorRole={editorRole}
            token={token}
            highlightSectionId={highlightedSectionId}
            showInlineHelp={canViewStaffHelp}
            onSaved={() => {
              loadMembership();
              loadStaffPermissionToggles();
            }}
            uiClasses={{ modalText, labelText, fieldClass, btnBase, cardClass }}
          />
        </div>
      )}
    </div>

  );



  if (embedded) {

    if (loading) {

      return <div className="text-slate-600">Loading...</div>;

    }

    if (!staff) {

      return <div className="text-slate-600">Staff not found.</div>;

    }

    return <div className="space-y-6">{content}</div>;

  }



  const headerLine = staff

    ? `Editing ${staff.display_name || "Staff"}`

    : loading

      ? "Loading staff details"

      : "Staff not found";



  return (

    <div className="staffEditPageShell min-h-screen bg-slate-100">
      <div className="staffEditPageWide staff-edit-page-compact">
        <div className="rounded-3xl bg-white shadow-2xl overflow-hidden flex flex-col">
        <div className="bg-rose-500 px-6 py-4 flex items-center justify-between gap-4">

          <div className="flex items-center gap-3 min-w-0">

            <button

              type="button"

              onClick={() => navigate("/manager/manage/staff")}

              className="h-10 px-4 rounded-full border border-white/60 text-white text-sm md:text-base font-semibold transition hover:bg-white/10 active:bg-white/20"

            >

              Back to Staff
            </button>

            <h1 className="flex min-w-0 items-center gap-2 text-xl md:text-2xl font-semibold text-white truncate">

              Edit Staff
              {canViewStaffHelp && (
                <InfoTooltip
                  text="Update login, details, schedule, services, and permissions."
                  label="Edit Staff"
                  maxWidth={300}
                />
              )}

            </h1>

          </div>

          <div className="flex items-center gap-2">
            <ModalCloseX onClick={() => navigate("/manager/manage/staff")} />
          </div>
        </div>
        <div className="px-6 py-6 text-lg md:text-xl font-semibold text-slate-900">
          {headerLine}
        </div>
        {staff && !loading && (
          <div className="px-6 pb-2 space-y-4">
            {(linkedSalonIds.length > 0 || linkedBusinessIds.length > 0) && (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                  Linked IDs
                  {canViewStaffHelp && (
                    <InfoTooltip
                      text="IDs used by support to find linked records."
                      label="Linked IDs"
                      maxWidth={260}
                    />
                  )}
                </div>
                <div className="mt-1 text-sm text-slate-500">
                  Shows the salon and business IDs linked to this staff member or linked user.
                </div>
                <div className="mt-3 flex flex-col gap-3 md:flex-row md:items-start md:gap-6">
                  <div className="min-w-0">
                    <div className="text-xs uppercase tracking-wide text-slate-400">
                      Salon IDs
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {linkedSalonIds.length > 0 ? (
                        linkedSalonIds.map((id) => (
                          <span
                            key={`linked-salon-${id}`}
                            className="inline-flex items-center rounded-full border border-slate-300 bg-white px-3 py-1 text-sm font-semibold text-slate-700"
                          >
                            {id}
                          </span>
                        ))
                      ) : (
                        <span className="text-sm text-slate-500">None</span>
                      )}
                    </div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs uppercase tracking-wide text-slate-400">
                      Business IDs
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {linkedBusinessIds.length > 0 ? (
                        linkedBusinessIds.map((id) => (
                          <span
                            key={`linked-business-${id}`}
                            className="inline-flex items-center rounded-full border border-slate-300 bg-white px-3 py-1 text-sm font-semibold text-slate-700"
                          >
                            {id}
                          </span>
                        ))
                      ) : (
                        <span className="text-sm text-slate-500">None</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
            {canViewStaffHelp && (
              <ReportHelpCard
                title="Help Guide: Staff Setup"
                subtitle="Use this guide to set up login, details, schedule, services, and permissions quickly."
                sections={helpSections}
                troubleshooting={STAFF_GUIDE_TROUBLESHOOTING}
                onJumpToSection={jumpToSectionWithHighlight}
              />
            )}
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-400">
                Quick Links
                {canViewStaffHelp && (
                  <InfoTooltip
                    text="Jump to a section on this page."
                    label="Quick Links"
                    maxWidth={240}
                  />
                )}
              </span>
              {staff?.user_id && (
                <button
                  type="button"
                  onClick={() => jumpToSectionWithHighlight("section-login")}
                  className="text-sm font-semibold text-slate-600 hover:text-slate-900 hover:underline underline-offset-2"
                >
                  Login
                </button>
              )}
              <button
                type="button"
                onClick={() => jumpToSectionWithHighlight("section-details")}
                className="text-sm font-semibold text-slate-600 hover:text-slate-900 hover:underline underline-offset-2"
              >
                Details
              </button>
              <button
                type="button"
                onClick={() => jumpToSectionWithHighlight("section-settings")}
                className="text-sm font-semibold text-slate-600 hover:text-slate-900 hover:underline underline-offset-2"
              >
                Settings
              </button>
              <button
                type="button"
                onClick={() => jumpToSectionWithHighlight("section-profile")}
                className="text-sm font-semibold text-slate-600 hover:text-slate-900 hover:underline underline-offset-2"
              >
                Profile
              </button>
              <button
                type="button"
                onClick={() => jumpToSectionWithHighlight("section-schedule")}
                className="text-sm font-semibold text-slate-600 hover:text-slate-900 hover:underline underline-offset-2"
              >
                Schedule
              </button>
              <button
                type="button"
                onClick={() => jumpToSectionWithHighlight("section-services")}
                className="text-sm font-semibold text-slate-600 hover:text-slate-900 hover:underline underline-offset-2"
              >
                Services
              </button>
              {linkedUserId && (
                <button
                  type="button"
                  onClick={() => jumpToSectionWithHighlight("section-permissions")}
                  className="text-sm font-semibold text-slate-600 hover:text-slate-900 hover:underline underline-offset-2"
                >
                  Permissions
                </button>
              )}
            </div>
          </div>
        )}
        <div className="px-6 pb-8 space-y-6">
          {loading ? (
            <div className="text-slate-600">Loading...</div>
          ) : !staff ? (
            <div className="text-slate-600">Staff not found.</div>

          ) : (

            content

          )}
        </div>
        </div>
      </div>
    </div>
  );
}


function StaffPermissionsSection({
  userId,
  editorRole,
  token,
  onSaved,
  uiClasses = {},
  highlightSectionId = "",
  showInlineHelp = false,
}) {
  const normalizedEditorRole = (editorRole || "").toLowerCase();

  const isManagerEditor = normalizedEditorRole === "manager";

  const [permissionsBundle, setPermissionsBundle] = useState(null);

  const [overrides, setOverrides] = useState({});

  const [loadingPermissions, setLoadingPermissions] = useState(false);

  const [error, setError] = useState("");

  const [status, setStatus] = useState("");

  const [saving, setSaving] = useState(false);

  const [resetting, setResetting] = useState(false);

  const [scheduleChoiceModal, setScheduleChoiceModal] = useState(null);



  const targetRole = normalizeSalonRole(

    permissionsBundle?.role || "technician"

  );

  const disableAll = isManagerEditor && targetRole === "owner";



  const loadPermissions = useCallback(async () => {

    if (!userId) {

      return;

    }

    setLoadingPermissions(true);

    setError("");

    try {

      const bundle = await getUserPermissions(userId, token);
      setPermissionsBundle(bundle);

      setOverrides(bundle.overrides || {});

      setStatus("");

    } catch (err) {

      setError(
        formatPermissionError(
          err,
          "Unable to load permissions. Please check your access and try again."
        )
      );

    } finally {

      setLoadingPermissions(false);

    }

  }, [userId, token]);


  useEffect(() => {

    setPermissionsBundle(null);

    setOverrides({});

    setStatus("");

    if (userId) {

      loadPermissions();

    }

  }, [userId, loadPermissions]);



  const rawEffectivePermissions = useMemo(() => {

    const base = { ...(permissionsBundle?.defaults || {}) };

    Object.entries(overrides || {}).forEach(([key, value]) => {

      base[key] = value;

    });

    return base;

  }, [permissionsBundle?.defaults, overrides]);

  const effectivePermissions = useMemo(() => {
    const next = { ...rawEffectivePermissions };
    const canSeeFullSchedule = Boolean(next[SCHEDULE_VIEW_ALL_PERMISSION]);
    const canSeeOwnSchedule = Boolean(next[SCHEDULE_VIEW_OWN_PERMISSION]);

    if (canSeeFullSchedule && canSeeOwnSchedule) {
      next[SCHEDULE_VIEW_OWN_PERMISSION] = false;
    } else if (!canSeeFullSchedule && !canSeeOwnSchedule) {
      next[SCHEDULE_VIEW_OWN_PERMISSION] = true;
    }

    return next;
  }, [rawEffectivePermissions]);

  const scheduleChoiceNeedsSave = useMemo(
    () =>
      SCHEDULE_VIEW_PERMISSIONS.some(
        (key) => Boolean(rawEffectivePermissions[key]) !== Boolean(effectivePermissions[key])
      ),
    [rawEffectivePermissions, effectivePermissions]
  );



  const hasChanges = useMemo(

    () =>

      scheduleChoiceNeedsSave ||
      JSON.stringify(overrides || {}) !==

      JSON.stringify(permissionsBundle?.overrides || {}),

    [overrides, permissionsBundle?.overrides, scheduleChoiceNeedsSave]

  );



  const setOverrideValue = (targetOverrides, key, value) => {
    const defaultValue = permissionsBundle?.defaults?.[key] ?? false;
    if (value === defaultValue) {
      delete targetOverrides[key];
    } else {
      targetOverrides[key] = value;
    }
  };

  const buildNormalizedOverridesForSave = (sourceOverrides = overrides) => {
    const next = { ...(sourceOverrides || {}) };
    setOverrideValue(
      next,
      SCHEDULE_VIEW_ALL_PERMISSION,
      Boolean(effectivePermissions[SCHEDULE_VIEW_ALL_PERMISSION])
    );
    setOverrideValue(
      next,
      SCHEDULE_VIEW_OWN_PERMISSION,
      Boolean(effectivePermissions[SCHEDULE_VIEW_OWN_PERMISSION])
    );
    return next;
  };



  const togglePermission = (key) => {

    if (disableAll) return;

    setStatus("");

    if (SCHEDULE_VIEW_PERMISSIONS.includes(key)) {
      const otherKey =
        key === SCHEDULE_VIEW_ALL_PERMISSION
          ? SCHEDULE_VIEW_OWN_PERMISSION
          : SCHEDULE_VIEW_ALL_PERMISSION;
      const nextValue = !effectivePermissions[key];

      if (!nextValue) {
        setScheduleChoiceModal({
          title: "Choose one schedule view",
          message:
            "Each staff member needs one schedule view option. Choose full schedule if they should see everyone, or own schedule if they should only see their appointments.",
        });
        return;
      }

      setOverrides((prev) => {
        const next = { ...(prev || {}) };
        setOverrideValue(next, key, true);
        setOverrideValue(next, otherKey, false);
        return next;
      });
      setScheduleChoiceModal({
        title: "Schedule view updated",
        message:
          "Only one schedule view option can be selected at a time. The other schedule view option was turned off for you.",
      });
      return;
    }

    setOverrides((prev) => {

      const next = { ...(prev || {}) };

      const defaultValue = permissionsBundle?.defaults?.[key] ?? false;

      const currentValue = effectivePermissions[key];

      const nextValue = !currentValue;

      if (nextValue === defaultValue) {

        delete next[key];

      } else {

        next[key] = nextValue;

      }

      return next;

    });

  };



  const handleSave = async () => {

    if (!userId) return;

    setSaving(true);

    setError("");

    try {

      const saveOverrides = buildNormalizedOverridesForSave();
      const result = await updateUserPermissions(userId, saveOverrides, token);
      setPermissionsBundle(result);

      setOverrides(result.overrides || {});

      setStatus("Permissions saved");

      onSaved?.();

    } catch (err) {

      setError(
        formatPermissionError(
          err,
          "Unable to save permissions. Please check your access and try again."
        )
      );

    } finally {

      setSaving(false);

    }

  };



  const handleReset = async () => {

    if (!userId) return;

    setResetting(true);

    setError("");

    try {

      const result = await resetUserPermissions(userId, token);
      setPermissionsBundle(result);

      setOverrides(result.overrides || {});

      setStatus("Permissions reset");

      onSaved?.();

    } catch (err) {

      setError(
        formatPermissionError(
          err,
          "Unable to reset permissions. Please check your access and try again."
        )
      );

    } finally {

      setResetting(false);

    }

  };



  if (!userId) {

    return null;

  }



  const {
    modalText = "text-base md:text-lg",
    btnBase = "h-11 md:h-12 px-6 text-base md:text-lg rounded-full",
    cardClass = "rounded-2xl border border-slate-200 bg-white shadow-sm p-6",
  } = uiClasses;
  const highlightedSectionClass =
    highlightSectionId === "section-permissions"
      ? "ring-2 ring-rose-300 ring-offset-2 transition-shadow duration-200"
      : "";

  return (
    <>
    <div className={`${cardClass} ${modalText} ${highlightedSectionClass} space-y-4`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-lg md:text-xl font-semibold text-slate-900">Permissions</h2>
            {showInlineHelp && (
              <InfoTooltip
                text="Grant only what is needed for this role."
                label="Permission groups"
                maxWidth={260}
              />
            )}
          </div>
          <p className="text-sm md:text-base text-slate-600">
            Adjust the effective permissions for this user.
          </p>
        </div>
        {(saving || resetting) && (

          <span className="text-xs md:text-sm text-slate-500">

            {saving ? "Saving..." : "Resetting..."}

          </span>
        )}
      </div>
      {showInlineHelp && (
        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
          Tip: Start with role defaults, then add only the minimum extra access needed.
        </div>
      )}

      {loadingPermissions ? (
        <p className="text-sm md:text-base text-slate-500">Loading permissions...</p>

      ) : error ? (

        <p className="text-sm md:text-base text-rose-600">{error}</p>

      ) : (

        <>

          {PERMISSION_GROUPS.map((group) => (

            <div key={group.title} className="space-y-2">

              <div className="flex items-center gap-2 text-sm md:text-base font-semibold text-slate-900 uppercase tracking-wide">

                {group.title}
                {showInlineHelp && (
                  <InfoTooltip
                    text={PERMISSION_GROUP_HELP[group.title] || "Controls access for this area."}
                    label={`${group.title} permissions`}
                    maxWidth={280}
                  />
                )}

              </div>

              <div className="grid gap-2 sm:grid-cols-2">

                {group.items.map((item) => {

                  const isOwnerOnly = OWNER_ONLY_PERMISSIONS.includes(item.key);

                  const disabled = disableAll || (isManagerEditor && isOwnerOnly);
                  const inputId = `staff-permission-${userId}-${item.key.replace(
                    /[^a-z0-9_-]/gi,
                    "-"
                  )}`;

                  return (

                    <div

                      key={item.key}

                      className={`flex items-center justify-between rounded-xl border px-3 py-2 text-sm md:text-base ${disabled

                          ? "border-slate-200 bg-slate-50 text-slate-400"

                          : "border-slate-200 bg-white text-slate-700"

                        }`}

                    >

                      <div className="flex min-w-0 items-center gap-2 pr-3">
                        <label
                          htmlFor={inputId}
                          className="min-w-0 leading-snug"
                          title={item.label}
                        >
                          {item.label}
                        </label>
                        {item.tooltip && (
                          <InfoTooltip
                            text={item.tooltip}
                            label={item.label}
                            maxWidth={300}
                            className="shrink-0"
                          />
                        )}
                      </div>

                      <input

                        id={inputId}

                        type="checkbox"

                        checked={!!effectivePermissions[item.key]}

                        onChange={() => togglePermission(item.key)}

                        disabled={disabled}

                        aria-label={item.label}

                        className="h-4 w-4 text-slate-900 border-slate-300 rounded"

                      />

                    </div>

                  );

                })}

              </div>

            </div>

          ))}

          {status && (

            <p className="text-sm md:text-base text-emerald-600">{status}</p>

          )}

          <div className="flex flex-wrap gap-2">

            <button

              type="button"

              onClick={handleSave}

              disabled={!hasChanges || saving || disableAll}

              className={`${btnBase} font-semibold text-white transition bg-slate-900 hover:bg-slate-800 disabled:opacity-50`}

            >

              {saving ? "Saving..." : "Save Permissions"}

            </button>

            <button

              type="button"

              onClick={handleReset}

              disabled={resetting || disableAll}

              className={`${btnBase} border border-slate-300 font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 disabled:opacity-50`}

            >

              {resetting ? "Resetting..." : "Reset to defaults"}

            </button>

          </div>

        </>

      )}

    </div>
    <SBINModal
      isOpen={Boolean(scheduleChoiceModal)}
      onClose={() => setScheduleChoiceModal(null)}
      title={scheduleChoiceModal?.title || "Schedule view"}
      size="sm"
      fitContent={true}
      footer={
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setScheduleChoiceModal(null)}
            className="rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            OK
          </button>
        </div>
      }
    >
      <p className="text-sm text-slate-700">
        {scheduleChoiceModal?.message || ""}
      </p>
    </SBINModal>
    </>

  );

}



export default function StaffEdit() {

  const { id } = useParams();

  const navigate = useNavigate();

  const { token, role, user } = useAuth();
  const normalizedRole = (role || "").toLowerCase();
  const { permissions: effectivePermissions, loading: permissionsLoading } =
    useMyPermissions({ enabled: Boolean(token && user?.salon_id) });
  const canAccessStaffEdit =
    ["admin", "host"].includes(normalizedRole) ||
    Boolean(user?.is_super_admin) ||
    (effectivePermissions === null
      ? MANAGER_ROLES.includes(normalizedRole)
      : Boolean(effectivePermissions?.["staff.manage"] || effectivePermissions?.["staff.update"]));


  useEffect(() => {

    if (!token) {

      navigate("/login?next=/manager/manage/staff", { replace: true });

      return;

    }

    if (permissionsLoading || effectivePermissions === null) {
      return;
    }

    if (!canAccessStaffEdit) {
      navigate("/my-bookings", { replace: true });

    }

  }, [
    token,
    navigate,
    permissionsLoading,
    effectivePermissions,
    canAccessStaffEdit,
  ]);


  return <StaffEditPanel staffId={id} />;

}
